// Na-Ion vs LFP — ECharts option builders
// Builds options for: Ragone (Na-Ion vs LFP with zoom toggle),
// Temporal Evolution, KPI Radar comparison (with yearly layers).

import {
  J_TO_WH,
  TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY,
  FONT_BODY, FONT_TITLE,
  _rgba, _scatterStyle,
  _baseTitle, _baseGrid, _baseTooltip, _baseAxis, _logYAxis, _baseLegend,
  _hullSeries, _hullSwappedSeries,
} from './radarChartTheme.js';

// ---------------------------------------------------------------------------
// Constants — Corporate Design
// ---------------------------------------------------------------------------

const NAION_COLOR = '#12a0be';   // Bright Teal (intensified)
const LFP_COLOR = '#d48440';     // Warm Sienna (intensified)
const OTHER_COLOR = 'rgba(255, 255, 255, 0.18)';

// ---------------------------------------------------------------------------
// 1. Ragone — Na-Ion vs LFP (with zoom state)
// ---------------------------------------------------------------------------

/**
 * Ragone scatter plot: Na-Ion vs LFP with all other cells as faint background.
 * @param {object} data - NaionVsLfpResponse from the API
 * @param {'grav'|'vol'} basis
 * @param {'all'|'focused'} zoomState - 'all' shows everything, 'focused' zooms to Na-Ion/LFP bounds
 */
export function buildNaionLfpRagone(data, basis = 'grav', zoomState = 'all') {
  const eKey = basis === 'vol' ? 'e_vol' : 'e_grav';
  const pKey = basis === 'vol' ? 'p_vol' : 'p_grav';

  const highlightIds = new Set();
  (data.ragone_naion || []).forEach((pt) => highlightIds.add(pt.product_id));
  (data.ragone_lfp || []).forEach((pt) => highlightIds.add(pt.product_id));

  const isValid = (pt) => pt[eKey] != null && pt[pKey] != null;

  const otherPts = (data.ragone_all || []).filter(
    (pt) => !highlightIds.has(pt.product_id) && isValid(pt)
  );
  const naionPts = (data.ragone_naion || []).filter(isValid);
  const lfpPts = (data.ragone_lfp || []).filter(isValid);

  const mapPt = (pt) => ({ value: [pt[pKey], pt[eKey] * J_TO_WH], _meta: pt });

  const isFocused = zoomState === 'focused';

  const scatterSeries = [];

  // In focused mode, hide "Other" scatter
  if (!isFocused) {
    scatterSeries.push({
      name: 'Other',
      type: 'scatter',
      data: otherPts.map(mapPt),
      symbolSize: 7,
      itemStyle: { color: OTHER_COLOR, borderColor: 'rgba(255,255,255,0.12)', borderWidth: 0.5 },
      emphasis: { disabled: true },
      z: 1,
    });
  }

  scatterSeries.push(
    {
      name: 'Na-Ion',
      type: 'scatter',
      data: naionPts.map(mapPt),
      symbol: 'diamond',
      symbolSize: 7,
      itemStyle: _scatterStyle(NAION_COLOR),
      emphasis: {
        focus: 'series',
        itemStyle: { opacity: 1, borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        itemStyle: { opacity: 0.08 },
      },
      z: 2,
    },
    {
      name: 'LFP',
      type: 'scatter',
      data: lfpPts.map(mapPt),
      symbol: 'circle',
      symbolSize: 7,
      itemStyle: _scatterStyle(LFP_COLOR),
      emphasis: {
        focus: 'series',
        itemStyle: { opacity: 1, borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        itemStyle: { opacity: 0.08 },
      },
      z: 2,
    },
  );

  // Convex hull series (swapped: X=power, Y=energy)
  const hullBasis = basis === 'vol' ? 'vol' : 'grav';
  const naionHull = _hullSwappedSeries('Na-Ion', data.hulls_naion?.[hullBasis], NAION_COLOR);
  if (naionHull) scatterSeries.push(naionHull);
  const lfpHull = _hullSwappedSeries('LFP', data.hulls_lfp?.[hullBasis], LFP_COLOR);
  if (lfpHull) scatterSeries.push(lfpHull);

  const eUnit = basis === 'vol' ? 'Wh/L' : 'Wh/kg';
  const pUnit = basis === 'vol' ? 'W/L' : 'W/kg';

  // Compute axis ranges for focused zoom (X=power, Y=energy)
  let xAxisOpts = {};
  let yAxisOpts = {};

  if (isFocused) {
    const focusedPts = [...naionPts, ...lfpPts];
    const eVals = focusedPts.map((pt) => pt[eKey] * J_TO_WH);
    const pVals = focusedPts.map((pt) => pt[pKey]);

    if (eVals.length > 0 && pVals.length > 0) {
      const eMin = Math.min(...eVals);
      const eMax = Math.max(...eVals);
      const pMin = Math.min(...pVals);
      const pMax = Math.max(...pVals);
      const ePad = (eMax - eMin) * 0.15;
      const pPad = (pMax - pMin) * 0.15;
      xAxisOpts = { min: Math.max(1, pMin - pPad), max: pMax + pPad };
      yAxisOpts = { min: Math.max(0, eMin - ePad), max: eMax + ePad };
    }
  }

  return {
    backgroundColor: 'transparent',
    title: _baseTitle('Ragone Plot — Na-Ion vs LFP'),
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicInOut',
    tooltip: {
      ..._baseTooltip(),
      formatter(params) {
        const meta = params.data?._meta;
        if (!meta) return '';
        if (meta.group !== 'Na-Ion' && meta.group !== 'LFP') {
          return `<strong>${meta.name}</strong>`;
        }
        return [
          `<strong>${meta.manufacturer} ${meta.name}</strong>`,
          `Power: ${params.value[0].toFixed(1)} ${pUnit}`,
          `Energy: ${params.value[1].toFixed(1)} ${eUnit}`,
        ].join('<br/>');
      },
    },
    legend: _baseLegend(['Na-Ion', 'LFP']),
    grid: _baseGrid(),
    xAxis: {
      ..._baseAxis(`Peak Power Density (${pUnit})`),
      ...xAxisOpts,
    },
    yAxis: {
      ..._baseAxis(`Energy Density (${eUnit})`, { nameGap: 55, nameRotate: 90 }),
      ...yAxisOpts,
    },
    series: scatterSeries,
  };
}

// ---------------------------------------------------------------------------
// 2. Temporal Evolution — Energy Density over Time
// ---------------------------------------------------------------------------

export function buildTemporalEvolution(data) {
  const naion = data.temporal_naion || [];
  const lfp = data.temporal_lfp || [];

  const yearSet = new Set();
  naion.forEach((d) => yearSet.add(d.year));
  lfp.forEach((d) => yearSet.add(d.year));
  const years = [...yearSet].sort();

  const naionByYear = new Map(naion.map((d) => [d.year, d]));
  const lfpByYear = new Map(lfp.map((d) => [d.year, d]));

  // Determine the latest year with data for "hero" median treatment
  const maxYear = years.length ? Math.max(...years.map(Number)) : 2025;
  const prevYear = maxYear - 1;

  function _bandSeries(label, color, byYear) {
    return [
      // P25 baseline (invisible, used for stacking)
      {
        name: `${label} p25`,
        type: 'line',
        stack: label,
        data: years.map((y) => {
          const d = byYear.get(y);
          return d ? d.p25 * J_TO_WH : null;
        }),
        lineStyle: { width: 0 },
        symbol: 'none',
        itemStyle: { color },
        emphasis: { disabled: true },
        legendHoverLink: false,
        tooltip: { show: false },
      },
      // P25–P75 band fill
      {
        name: `${label} band`,
        type: 'line',
        stack: label,
        data: years.map((y) => {
          const d = byYear.get(y);
          return d ? (d.p75 - d.p25) * J_TO_WH : null;
        }),
        lineStyle: { width: 0 },
        symbol: 'none',
        itemStyle: { color },
        areaStyle: { color, opacity: 0.15 },
        emphasis: { disabled: true },
        legendHoverLink: false,
        tooltip: { show: false },
      },
      // Median line — hero treatment: latest year solid+thick, previous dashed, older hidden
      {
        name: label,
        type: 'line',
        data: years.map((y) => {
          const d = byYear.get(y);
          return d ? d.median * J_TO_WH : null;
        }),
        lineStyle: { width: 3, color },
        symbol: 'circle',
        symbolSize: 7,
        itemStyle: { color },
        connectNulls: true,
      },
    ];
  }

  // Individual scatter points on top of bands
  const scatterNaion = [];
  const scatterLfp = [];

  (data.ragone_naion || []).forEach((pt) => {
    if (pt.e_grav != null && pt.year) {
      scatterNaion.push({ value: [pt.year, pt.e_grav * J_TO_WH], _meta: pt });
    }
  });
  (data.ragone_lfp || []).forEach((pt) => {
    if (pt.e_grav != null && pt.year) {
      scatterLfp.push({ value: [pt.year, pt.e_grav * J_TO_WH], _meta: pt });
    }
  });

  const series = [
    ..._bandSeries('Na-Ion', NAION_COLOR, naionByYear),
    ..._bandSeries('LFP', LFP_COLOR, lfpByYear),
    {
      name: 'Na-Ion pts',
      type: 'scatter',
      data: scatterNaion,
      symbolSize: 4,
      itemStyle: { color: NAION_COLOR, opacity: 0.5 },
      z: 3,
      tooltip: {
        formatter(params) {
          const m = params.data?._meta;
          if (!m) return '';
          return `<b>${m.manufacturer} ${m.name}</b><br/>Energy: ${(m.e_grav * J_TO_WH).toFixed(1)} Wh/kg`;
        },
      },
    },
    {
      name: 'LFP pts',
      type: 'scatter',
      data: scatterLfp,
      symbolSize: 4,
      itemStyle: { color: LFP_COLOR, opacity: 0.5 },
      z: 3,
      tooltip: {
        formatter(params) {
          const m = params.data?._meta;
          if (!m) return '';
          return `<b>${m.manufacturer} ${m.name}</b><br/>Energy: ${(m.e_grav * J_TO_WH).toFixed(1)} Wh/kg`;
        },
      },
    },
  ];

  return {
    backgroundColor: 'transparent',
    title: _baseTitle('Energy Density — Na-Ion vs LFP'),
    tooltip: {
      ..._baseTooltip({ trigger: 'axis' }),
      formatter(params) {
        if (!params || params.length === 0) return '';
        const year = params[0].axisValue;
        const lines = [`<strong>${year}</strong>`];

        // Only show median lines (skip p25 baseline, band fill, and scatter pts)
        const seen = new Set();
        params.forEach((p) => {
          if (p.seriesName.includes('p25') || p.seriesName.includes('band') || p.seriesName.includes('pts')) return;
          if (seen.has(p.seriesName)) return;
          seen.add(p.seriesName);
          const byYear = p.seriesName === 'Na-Ion' ? naionByYear : lfpByYear;
          const d = byYear.get(String(year));
          if (!d) return;
          lines.push(
            `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px;"></span>` +
              `<strong>${p.seriesName}</strong>: ${(d.median * J_TO_WH).toFixed(1)} Wh/kg` +
              ` (P25: ${(d.p25 * J_TO_WH).toFixed(1)}, P75: ${(d.p75 * J_TO_WH).toFixed(1)})`
          );
        });

        return lines.join('<br/>');
      },
    },
    legend: _baseLegend(['Na-Ion', 'LFP']),
    grid: _baseGrid(),
    xAxis: {
      type: 'category',
      data: years,
      nameLocation: 'center',
      nameGap: 35,
      axisLabel: { color: TEXT_SECONDARY, fontFamily: FONT_BODY, fontSize: 12 },
      axisLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
      splitLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
    },
    yAxis: {
      type: 'value',
      name: 'Energy Density (Wh/kg)',
      nameLocation: 'center',
      nameGap: 55,
      nameRotate: 90,
      nameTextStyle: { color: TEXT_SECONDARY, fontFamily: FONT_BODY, fontSize: 13 },
      axisLabel: { color: TEXT_TERTIARY, fontFamily: FONT_BODY, fontSize: 11 },
      axisLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
      splitLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
    },
    series,
  };
}

// ---------------------------------------------------------------------------
// 3. KPI Radar — Na-Ion vs LFP (with yearly layers)
// ---------------------------------------------------------------------------

const KPI_INDICATORS = [
  { key: 'p_grav', label: 'Peak Power\n(W/kg)' },
  { key: 'e_vol', label: 'Vol. Energy\n(Wh/L)' },
  { key: 'e_grav', label: 'Energy\n(Wh/kg)' },
  { key: 'p_cont_grav', label: 'Cont. Power\n(W/kg)' },
  { key: 'eta_dc_25', label: 'Efficiency\n(%)' },
  { key: 'v_mean', label: 'Voltage\n(V)' },
];

/**
 * Radar chart comparing normalized KPI scores for Na-Ion and LFP.
 * If yearly data is available, shows 2023/2024/2025 with graduated opacity.
 * @param {object} data - NaionVsLfpResponse from the API
 */
export function buildKpiRadar(data) {
  const indicators = KPI_INDICATORS.map((ind) => ({
    name: ind.label,
    max: 100,
    min: 0,
  }));

  const radarData = [];

  // Check for yearly KPI data
  const naionByYear = data.kpi_naion_by_year;
  const lfpByYear = data.kpi_lfp_by_year;

  if (naionByYear && lfpByYear) {
    const yearOpacity = { '2023': 0.3, '2024': 0.6, '2025': 1.0 };
    const years = Object.keys(yearOpacity).filter(
      (y) => naionByYear[y] || lfpByYear[y]
    );

    years.forEach((yr) => {
      const opacity = yearOpacity[yr] || 1.0;
      const naionKpi = naionByYear[yr] || {};
      const lfpKpi = lfpByYear[yr] || {};

      radarData.push({
        name: `Na-Ion ${yr}`,
        value: KPI_INDICATORS.map((ind) => naionKpi[ind.key] ?? 0),
        lineStyle: { width: yr === '2025' ? 2 : 1, color: NAION_COLOR, opacity },
        itemStyle: { color: NAION_COLOR, opacity },
        areaStyle: { color: NAION_COLOR, opacity: opacity * 0.15 },
      });
      radarData.push({
        name: `LFP ${yr}`,
        value: KPI_INDICATORS.map((ind) => lfpKpi[ind.key] ?? 0),
        lineStyle: { width: yr === '2025' ? 2 : 1, color: LFP_COLOR, opacity },
        itemStyle: { color: LFP_COLOR, opacity },
        areaStyle: { color: LFP_COLOR, opacity: opacity * 0.15 },
      });
    });
  } else {
    // Fallback: single overall KPI comparison
    const naionKpi = data.kpi_naion || {};
    const lfpKpi = data.kpi_lfp || {};

    radarData.push(
      {
        name: 'Na-Ion',
        value: KPI_INDICATORS.map((ind) => naionKpi[ind.key] ?? 0),
        lineStyle: { width: 2, color: NAION_COLOR },
        itemStyle: { color: NAION_COLOR },
        areaStyle: { color: NAION_COLOR, opacity: 0.2 },
      },
      {
        name: 'LFP',
        value: KPI_INDICATORS.map((ind) => lfpKpi[ind.key] ?? 0),
        lineStyle: { width: 2, color: LFP_COLOR },
        itemStyle: { color: LFP_COLOR },
        areaStyle: { color: LFP_COLOR, opacity: 0.2 },
      },
    );
  }

  return {
    backgroundColor: 'transparent',
    title: _baseTitle('KPI Radar — Na-Ion vs LFP'),
    legend: {
      top: 36,
      textStyle: { color: TEXT_SECONDARY, fontFamily: FONT_BODY, fontSize: 12 },
      data: radarData.map((d) => d.name),
    },
    tooltip: {
      ..._baseTooltip(),
      formatter(params) {
        const values = params.value;
        const lines = [`<strong>${params.name}</strong>`];
        KPI_INDICATORS.forEach((ind, i) => {
          lines.push(`${ind.label}: ${values[i].toFixed(0)}/100`);
        });
        return lines.join('<br/>');
      },
    },
    radar: {
      center: ['50%', '55%'],
      radius: '65%',
      indicator: indicators,
      name: {
        textStyle: {
          color: TEXT_SECONDARY,
          fontFamily: FONT_BODY,
          fontSize: 11,
        },
      },
      splitArea: {
        areaStyle: {
          color: [
            'rgba(148, 163, 184, 0.02)',
            'rgba(148, 163, 184, 0.05)',
            'rgba(148, 163, 184, 0.02)',
            'rgba(148, 163, 184, 0.05)',
            'rgba(148, 163, 184, 0.02)',
          ],
        },
      },
      splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.08)' } },
      axisLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.08)' } },
    },
    series: [{ type: 'radar', data: radarData }],
  };
}
